import { z, ZodTypeAny } from "zod";
import type { QuizFieldType } from "../quiz.types";
import { affiliationEntrySchema } from "@core/features/affiliations";

/**
 * Dynamically builds a Zod schema from quiz field definitions.
 * Creates validation rules based on field type and validation_rules.
 */
export function buildFormSchemaFromFields(fields: QuizFieldType[]) {
  const shape: Record<string, ZodTypeAny> = {};

  for (const field of fields) {
    const {
      name,
      required,
      type,
      validation_rules: rules,
      choices,
      isForceYes,
      maxWords,
    } = field;

    let schema: ZodTypeAny;
    const requiredMsg = "This field is required";

    switch (type) {
      case "text input": {
        let textSchema = z.string();

        if (rules?.min_characters != null) {
          textSchema = textSchema.min(rules.min_characters, {
            message: `Minimum ${rules.min_characters} characters`,
          });
        } else if (required) {
          textSchema = textSchema.min(1, { message: requiredMsg });
        }

        if (rules?.max_characters != null) {
          textSchema = textSchema.max(rules.max_characters, {
            message: `Maximum ${rules.max_characters} characters`,
          });
        }

        schema = required ? textSchema : textSchema.optional();
        break;
      }

      case "url": {
        // URL validation requiring protocol (e.g., https://example.com)
        let urlSchema = z.string().url({ message: "Please enter a valid URL" });

        if (required) {
          urlSchema = urlSchema.min(1, { message: requiredMsg });
        }

        schema = required ? urlSchema : urlSchema.optional();
        break;
      }

      case "longtext": {
        let longtextSchema = z.string();

        if (rules?.min_characters != null) {
          longtextSchema = longtextSchema.min(rules.min_characters, {
            message: `Minimum ${rules.min_characters} characters`,
          });
        } else if (required) {
          longtextSchema = longtextSchema.min(1, { message: requiredMsg });
        }

        if (rules?.max_characters != null) {
          longtextSchema = longtextSchema.max(rules.max_characters, {
            message: `Maximum ${rules.max_characters} characters`,
          });
        }

        // Word count validation (max_words comes from config_object via field.maxWords)
        if (maxWords != null) {
          schema = longtextSchema.refine(
            (text) => {
              const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
              return wordCount <= maxWords;
            },
            {
              message: `Maximum ${maxWords} words`,
            }
          );
          if (!required) {
            schema = schema.optional();
          }
        } else {
          schema = required ? longtextSchema : longtextSchema.optional();
        }
        break;
      }

      case "yes/no":
      case "yes/no-buttons":
      case "multiple choice": {
        if (isForceYes) {
          schema = z.any();
          break;
        }

        const validChoices = choices?.map((choice) => choice.id);

        schema = z
          .array(z.number())
          // Required
          .refine((arr) => !required || arr.length > 0, {
            message: "Please select at least one option",
          })
          // All values must be valid if any are present
          .refine(
            (arr) => {
              // allow empty array
              if (arr.length === 0) return true;

              return arr.every((val) => validChoices?.includes(val));
            },
            {
              message: "Please select valid option(s)",
            }
          );

        if (rules?.min_select != null) {
          schema = schema.refine((arr) => arr.length >= rules.min_select!, {
            message: `Select at least ${rules.min_select} option(s)`,
          });
        }

        if (rules?.max_select != null && rules.max_select !== "none") {
          schema = schema.refine((arr) => arr.length <= rules.max_select!, {
            message: `Select up to ${rules.max_select} option(s)`,
          });
        }

        // Optional schema wrapper, only if the field is not required
        if (!required) {
          schema = schema.optional();
        }

        break;
      }

      case "organization selector": {
        schema = z
          .array(affiliationEntrySchema.partial())
          .refine(
            (arr) => arr.some((entry) => !!entry.organization && !!entry.role),
            {
              message: "Please add at least one organization with a role",
            }
          );

        if (rules?.min_select != null) {
          schema = schema.refine(
            (arr) =>
              arr.filter((e) => !!e.organization && !!e.role).length >=
              rules.min_select!,
            {
              message: `Please add at least ${rules.min_select} organization(s)`,
            }
          );
        }

        if (rules?.max_select != null && rules.max_select !== "none") {
          schema = schema.refine(
            (arr) =>
              arr.filter((e) => !!e.organization && !!e.role).length <=
              rules.max_select!,
            {
              message: `Please add no more than ${rules.max_select} organization(s)`,
            }
          );
        }

        if (!required) {
          schema = schema.optional();
        }

        break;
      }

      case "image":
      case "file": {
        // File-based answers store an array of media IDs
        schema = z.array(z.number());

        if (required) {
          schema = schema.refine((arr) => arr.length > 0, {
            message: "Please upload at least one file",
          });
        }

        if (rules?.min_files != null) {
          schema = schema.refine((arr) => arr.length >= rules.min_files!, {
            message: `Please upload at least ${rules.min_files} file(s)`,
          });
        }

        if (rules?.max_files != null) {
          schema = schema.refine((arr) => arr.length <= rules.max_files!, {
            message: `Please upload no more than ${rules.max_files} file(s)`,
          });
        }

        if (!required) {
          schema = schema.optional();
        }

        break;
      }

      default: {
        schema = z.any();
        break;
      }
    }

    shape[name] = schema;
  }

  return z.object(shape);
}

